import React, { useState } from 'react';
import {
  Badge,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CommitIcon from '@mui/icons-material/Commit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { zoneColor } from './hillGeometry';
import { prChipStyle, prStateSummary } from './prPresentation';
import AssigneePicker from './AssigneePicker';

/**
 * One scope as a compact, square-ish card. The hill answers "how is this going";
 * a card answers "what is this and what's left", and clicking it opens the drawer
 * with the full detail. Todo progress is shown when the parent has already
 * fetched the bet's todos; otherwise it waits on a skeleton.
 */
const ScopeCard = ({ scope, todos = null, selected = false, onClick, onRename, loadingTodos = false, members = [] }) => {
  const total = todos ? todos.length : 0;
  const done = todos ? todos.filter(todo => todo.done).length : 0;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const canRename = Boolean(onRename);

  const startEditing = () => {
    setDraft(scope.jiraSummary || '');
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    onRename(scope, draft);
  };

  return (
    <Paper
      variant="outlined"
      onClick={() => !editing && onClick && onClick(scope)}
      sx={{
        p: 1.5,
        minHeight: 120,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        cursor: editing ? 'default' : 'pointer',
        borderRadius: 2,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        backgroundImage: 'none',
        transition: 'border-color 0.15s ease',
        '&:hover': { borderColor: selected ? 'primary.main' : '#404040' },
        '&:hover .scope-card-edit': { opacity: 1 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            flexShrink: 0,
            bgcolor: zoneColor(scope.position ?? 0),
          }}
        />
        {scope.jiraStatus && (
          <Chip
            size="small"
            variant="outlined"
            label={scope.jiraStatus}
            sx={{ textTransform: 'capitalize', maxWidth: '100%' }}
          />
        )}
      </Box>

      {editing ? (
        <TextField
          size="small"
          fullWidth
          autoFocus
          multiline
          value={draft}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape') setEditing(false);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); commit(); }}
                  aria-label="Save scope name"
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setEditing(false); }}
                  aria-label="Cancel rename"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: selected ? 700 : 500,
              flexGrow: 1,
              minWidth: 0,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {scope.jiraSummary || scope.jiraKey}
          </Typography>
          {canRename && (
            <Tooltip title={scope.jiraKey ? 'Rename this issue in Jira' : 'Rename this scope'}>
              <IconButton
                size="small"
                className="scope-card-edit"
                onClick={(e) => { e.stopPropagation(); startEditing(); }}
                aria-label="Rename scope"
                sx={{ p: 0.25, flexShrink: 0, opacity: 0, transition: 'opacity 0.15s ease' }}
              >
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {(scope.isParked || scope.isStuck || scope.isProvisional) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {scope.isParked && <Chip size="small" variant="outlined" label="Parked" />}
          {scope.isStuck && <Chip size="small" variant="outlined" color="warning" label="Stuck" />}
          {scope.isProvisional && <Chip size="small" variant="outlined" label="Guessed" />}
        </Box>
      )}

      {scope.pullRequests && scope.pullRequests.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {scope.pullRequests.slice(0, 3).map((pr) => {
            const { color, label } = prChipStyle(pr);
            return (
              <Tooltip key={pr.id} title={`${pr.title || 'Pull request'} — ${prStateSummary(pr)}`}>
                <Chip
                  size="small"
                  variant="outlined"
                  color={color}
                  icon={<CallSplitIcon sx={{ fontSize: 13 }} />}
                  label={`#${pr.number} ${label}`}
                  component="a"
                  href={pr.url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  onClick={(e) => e.stopPropagation()}
                  sx={{ maxWidth: '100%' }}
                />
              </Tooltip>
            );
          })}
          {scope.pullRequests.length > 3 && (
            <Chip
              size="small"
              variant="outlined"
              label={`+${scope.pullRequests.length - 3} more`}
            />
          )}
        </Box>
      )}

      {/* A quiet signal that code exists for this scope even before a PR is open,
          so a branch or a few commits do not go unnoticed on the card. */}
      {scope.commits && scope.commits.length > 0 && (
        <Chip
          size="small"
          variant="outlined"
          icon={<CommitIcon sx={{ fontSize: 13 }} />}
          label={`${scope.commits.length}`}
          sx={{ alignSelf: 'flex-start' }}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {loadingTodos && !todos ? (
          <Skeleton variant="text" width={28} />
        ) : (
          total > 0 && (
            <Typography variant="caption" color={done === total ? 'success.main' : 'text.secondary'}>
              {`${done}/${total}`}
            </Typography>
          )
        )}

        {scope.pullRequests?.some(pr => pr.needsYourReview) && (
          <Tooltip title="You have PRs to review on this scope">
            <Badge color="warning" variant="dot">
              <RateReviewIcon sx={{ fontSize: 15, color: 'warning.main' }} />
            </Badge>
          </Tooltip>
        )}

        {scope.dodMet && (
          <Tooltip title="Meets the definition of done">
            <CheckCircleIcon color="success" sx={{ fontSize: 15 }} />
          </Tooltip>
        )}

        {scope.commentCount > 0 && (
          <Tooltip
            title={
              scope.unreadCommentCount > 0
                ? `${scope.unreadCommentCount} unread of ${scope.commentCount} comment${scope.commentCount === 1 ? '' : 's'}`
                : `${scope.commentCount} comment${scope.commentCount === 1 ? '' : 's'}`
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: 'text.secondary' }}>
              <Badge
                color="error"
                variant="dot"
                overlap="circular"
                invisible={!(scope.unreadCommentCount > 0)}
              >
                <ChatBubbleIcon sx={{ fontSize: 13 }} />
              </Badge>
              <Typography variant="caption" color="text.secondary">{scope.commentCount}</Typography>
            </Box>
          </Tooltip>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <AssigneePicker target={scope} members={members} size={20} />

        {scope.jiraKey && (
          <Link
            href={scope.jiraUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            onClick={(e) => e.stopPropagation()}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}
          >
            {scope.jiraKey}
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Link>
        )}
      </Box>
    </Paper>
  );
};

export default ScopeCard;
